import React, { useState, useEffect } from "react";
import { Card, Grid, CircularProgress } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import api from "utils/api";

// Layout Components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Custom Components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonInput from "components/ArgonInput";
import ArgonButton from "components/ArgonButton";
import { Category } from "@mui/icons-material";

function EditSupportTicketCategory() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [formData, setFormData] = useState({
    name: "",
  });

  useEffect(() => {
    fetchCategory();
  }, [id]);

  const fetchCategory = async () => {
    setFetching(true);
    try {
      const response = await api.get(`/support-ticket-categories/${id}`);
      setFormData({
        name: response.data.data.name || "",
      });
    } catch (error) {
      console.error("Error fetching support ticket category:", error);
      toast.error("Failed to fetch support ticket category");
      navigate("/useful-corners/support-ticket-category");
    } finally {
      setFetching(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error("Category name is required");
      return;
    }

    setLoading(true);
    try {
      await api.put(`/support-ticket-categories/${id}`, formData);
      toast.success("Support ticket category updated successfully!");
      navigate("/useful-corners/support-ticket-category");
    } catch (error) {
      console.error("Error updating support ticket category:", error);
      toast.error(error.response?.data?.message || "Failed to update support ticket category");
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <ArgonBox py={3}>
          <ArgonBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
            <CircularProgress />
          </ArgonBox>
        </ArgonBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox
              p={3}
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                borderRadius: "12px 12px 0 0",
              }}
            >
              <ArgonBox display="flex" alignItems="center" gap={2}>
                <Category sx={{ fontSize: 32, color: "#fff" }} />
                <ArgonTypography variant="h5" color="white" fontWeight="bold">
                  Edit Support Ticket Category
                </ArgonTypography>
              </ArgonBox>
            </ArgonBox>

            <ArgonBox p={4}>
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <ArgonBox mb={2}>
                      <ArgonTypography variant="caption" fontWeight="bold" color="text">
                        Category Name *
                      </ArgonTypography>
                      <ArgonInput
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Enter category name"
                        required
                        fullWidth
                      />
                    </ArgonBox>
                  </Grid>
                </Grid>

                <ArgonBox display="flex" justifyContent="flex-end" gap={2} mt={4}>
                  <ArgonButton
                    variant="outlined"
                    color="secondary"
                    onClick={() => navigate("/useful-corners/support-ticket-category")}
                    disabled={loading}
                  >
                    Cancel
                  </ArgonButton>
                  <ArgonButton type="submit" variant="gradient" color="info" disabled={loading}>
                    {loading ? "Updating..." : "Update Category"}
                  </ArgonButton>
                </ArgonBox>
              </form>
            </ArgonBox>
          </Card>
        </ArgonBox>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default EditSupportTicketCategory;
